// PDFs inside the installed app (PWA, «На экран Домой»): there is no browser bar
// there, so a PDF opened as a page, a new tab or a download fills the screen with
// no way back (iPhone). In that mode every PDF opens in Core's own viewer instead
// (PdfViewerHost: pages drawn by pdf.js from /pdfjs, «Назад», «Поделиться»).
// In a normal browser tab nothing changes.

const EVENT = 'cp:open-pdf';
let nativeOpen = null;
let nativeClick = null;

export const isStandalone = () =>
  Boolean(
    (window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true ||
    /CherryPickApp\//.test(window.navigator.userAgent || '') // the Android app's WebView
  );

// a link to a PDF: *.pdf, our …/pdf endpoints, or a download named *.pdf
export function isPdfLink(href, download) {
  if (download && /\.pdf$/i.test(download)) return true;
  try {
    const u = new URL(href, window.location.href);
    if (u.protocol === 'blob:') return false; // only with a *.pdf download name (above)
    if (/\.pdf$/i.test(u.pathname)) return true;
    return u.origin === window.location.origin && /\/pdf\/?$/.test(u.pathname);
  } catch (e) {
    return false;
  }
}

export function openPdf({ url = null, blob = null, name = '' }) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { url, blob, name } }));
}

export const onOpenPdf = (fn) => {
  const h = (e) => fn(e.detail);
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
};

// open the link in a real browser window (the viewer's fallback)
export const openOutside = (url) =>
  (nativeOpen || window.open).call(window, url, '_blank', 'noopener');

// click an anchor the normal way (the viewer's own «Скачать»)
export const clickNative = (a) => (nativeClick || a.click).call(a);

function openAnchor(a) {
  const name = a.getAttribute('download') || '';
  if (a.href.startsWith('blob:')) {
    // read the blob now — the page may revoke the link right after the click
    fetch(a.href)
      .then((r) => r.blob())
      .then((blob) => openPdf({ blob, name }))
      .catch(() => {});
  } else {
    openPdf({ url: a.href, name });
  }
}

export function installPdfInterceptor() {
  if (!isStandalone() || window.__cpPdfViewer) return;
  window.__cpPdfViewer = true;

  // links on the page
  document.addEventListener(
    'click',
    (e) => {
      const a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a || e.defaultPrevented) return;
      if (!isPdfLink(a.href, a.getAttribute('download'))) return;
      e.preventDefault();
      openAnchor(a);
    },
    true
  );

  // downloads made in code: a detached <a download="x.pdf"> clicked by script
  nativeClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function click() {
    if (
      !this.isConnected &&
      isPdfLink(this.href, this.getAttribute('download'))
    ) {
      openAnchor(this);
      return undefined;
    }
    return nativeClick.call(this);
  };

  // window.open(pdfUrl)
  nativeOpen = window.open;
  window.open = function open(url, ...rest) {
    if (url && isPdfLink(String(url))) {
      openPdf({ url: new URL(String(url), window.location.href).href });
      return null;
    }
    return nativeOpen.call(window, url, ...rest);
  };
}

let pdfjs = null;
export function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!pdfjs) {
    pdfjs = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = '/pdfjs/pdf.min.js';
      s.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          '/pdfjs/pdf.worker.min.js';
        resolve(window.pdfjsLib);
      };
      s.onerror = () => {
        pdfjs = null;
        reject(new Error('Просмотрщик PDF не загрузился — проверьте интернет'));
      };
      document.head.appendChild(s);
    });
  }
  return pdfjs;
}
